import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  input,
  output,
  effect,
  signal,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ClientService, ToastMessageService } from '@fitness-force/api';
import { ErrorMsgComponent } from '@fitness-force/errors';
import {
  SpecialCharacterCheck,
  MinimumNumericalCheck,
  MinimumLowerCaseCheck,
  ConfirmedValidator,
  MinimumUpperCaseCheck,
} from '@fitness-force/shared-utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';

@Component({
  selector: 'client-profile-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    AvatarModule,
    ButtonModule,
    RippleModule,
    BlockUIModule,
    StyleClassModule,
    InputTextModule,
    PasswordModule,
    ErrorMsgComponent,
    MessageModule,
    TranslateModule,
  ],
})
export class ChangePasswordComponent implements OnInit {
  readonly changePasswordModal = input<boolean | null>(false);
  readonly clientDetails = input<any>();
  readonly tenantId = input<number>(0);
  readonly closePopup = output({ alias: 'closePopup' });

  readonly closechangePasswordDialog = output();

  resetForm: UntypedFormGroup = new UntypedFormGroup({});

  // Angular 21: Use signals for component state
  btnLoading = signal(false);
  private isClosing = signal(false);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly clientService: ClientService,
    private readonly toastMessageService: ToastMessageService,
    public readonly translate: TranslateService,
  ) {
    // Angular 21: Use effect in constructor to watch for dialog visibility changes
    effect(() => {
      const isVisible = this.changePasswordModal();
      if (!isVisible) {
        this.isClosing.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.resetForm = this.fb.group(
      {
        new_password: ['', [Validators.required]],
        confirm_password: ['', [Validators.required]],
      },
      {
        validator: [
          SpecialCharacterCheck('new_password', 1),
          MinimumNumericalCheck('new_password', 1),
          MinimumLowerCaseCheck('new_password', 1),
          MinimumUpperCaseCheck('new_password', 1),
          SpecialCharacterCheck('confirm_password', 1),
          MinimumNumericalCheck('confirm_password', 1),
          MinimumLowerCaseCheck('confirm_password', 1),
          MinimumUpperCaseCheck('confirm_password', 1),
          ConfirmedValidator('new_password', 'confirm_password'),
        ],
      },
    );
  }

  ValidateAndSubmit(): void {
    this.resetForm.markAllAsTouched();
    if (this.resetForm.valid) {
      this.btnLoading.set(true);
      const newPassword = this.NewPassword.value;
      this.clientService
        .ChangePassword(this.tenantId(), this.clientDetails().id, newPassword)
        .subscribe({
          next: async () => {
            this.translate
              .get('STAFF.CHANGEPASSWORD.SUCCESS_MSG')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });

            await this.timeout(1000);
            this.btnLoading.set(false);
            this.closeDialogEvent();
          },
          error: () => {
            this.translate
              .get('common.errors.SERVER_SIDE.SOMETHING_WRONG')
              .subscribe((translation) => {
                this.toastMessageService.showErrorMessage(translation);
              });
            this.btnLoading.set(false);
          },
        });
    }
  }

  ClosePopup(): void {
    this.closeDialogEvent();
  }

  closeDialogEvent(): void {
    // Angular 21: Prevent multiple close events from being emitted
    // Only emit if dialog is actually visible
    const isVisible = this.changePasswordModal();
    if (this.isClosing() || !isVisible) {
      return;
    }
    this.isClosing.set(true);
    this.resetForm.reset();
    this.closePopup.emit();
    // The effect in constructor will reset isClosing when dialog closes
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  get NewPassword() {
    return this.resetForm.controls.new_password;
  }

  get NewConfirmPassword() {
    return this.resetForm.controls.confirm_password;
  }
}
